import * as React from "react";
import { createFileRoute, Link } from "@tanstack/react-router";
import { Check, Pencil } from "lucide-react";
import { toast } from "sonner";

import { AppShell } from "@/components/layout/app-shell";
import { ApplyLateFeeDialog } from "@/components/apply-late-fee-dialog";
import { FeeLinesEditor, FeeLinesReadOnly } from "@/components/fee-lines-editor";
import { TieredFeeEditor, TieredFeeReadOnly } from "@/components/tiered-fee-editor";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Segmented } from "@/components/ui/segmented";
import { Skeleton } from "@/components/ui/skeleton";
import { StatCard } from "@/components/ui/stat-card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { usePendingPolicyStore } from "@/state/pending-policy-store";
import {
  useGetInvoices,
  useGetBankAccounts,
  useGetAccount,
  useUpdateWorkflowFeesSettings,
  useGetWorkflowFeesSettings,
} from "@/lib/api/generated/default/default";
import { PaymentFeeType } from "@/lib/api/generated/model";
import { browserLocale, formatMoney, FIXED_FEE_TYPES, VARIABLE_FEE_TYPES } from "@/lib/late-fee";
import {
  settingsToPolicyDraft,
  policyDraftToSettingsIn,
  pendingFeesToSettingsIn,
  type FeeLineDraft,
  type PolicyDraft,
} from "@/lib/fee-mapping";
import { cn } from "@/lib/utils";
import { TreasureInvoiceOut } from "@/lib/api/generated/model/treasureInvoiceOut";

export const Route = createFileRoute("/invoices/")({
  head: () => ({
    meta: [
      { title: "Invoices — Treasure" },
      {
        name: "description",
        content: "Every overdue invoice, with the fee already worked out.",
      },
    ],
  }),
  component: InvoicesIndex,
});

function InvoicesIndex() {
  const { data: account } = useGetAccount();
  const invoicesQuery = useGetInvoices({
    query: {
      refetchInterval: (query) => ((query.state.data?.length ?? 0) === 0 ? 10_000 : false),
    },
  });
  const invoices = invoicesQuery.data ?? [];
  const settingsQuery = useGetWorkflowFeesSettings({ workflow_id: null });
  const accountsQuery = useGetBankAccounts();
  const accounts = accountsQuery.data ?? {};
  const orgCurrency = account?.base_currency ?? "GBP";
  const locale = browserLocale();
  const updateSettings = useUpdateWorkflowFeesSettings();
  const pendingFees = usePendingPolicyStore((s) => s.pendingFees);
  const clearPendingFees = usePendingPolicyStore((s) => s.clearPendingFees);

  const policy = React.useMemo(
    () => settingsToPolicyDraft(settingsQuery.data),
    [settingsQuery.data],
  );

  const [editing, setEditing] = React.useState(false);
  const [draft, setDraft] = React.useState<PolicyDraft>(policy);
  const [applyInvoice, setApplyInvoice] = React.useState<TreasureInvoiceOut | null>(null);

  // Sync the draft to the loaded policy whenever we're not mid-edit.
  React.useEffect(() => {
    if (!editing) setDraft(policy);
  }, [policy, editing]);

  const appliedPendingFeesRef = React.useRef(false);
  const settingsLoading = settingsQuery.isLoading;
  const settingsError = settingsQuery.isError;
  const settingsData = settingsQuery.data;
  React.useEffect(() => {
    if (settingsLoading || settingsError) return;

    if (settingsData) {
      if (pendingFees) clearPendingFees();
      return;
    }

    if (appliedPendingFeesRef.current || !pendingFees) return;
    appliedPendingFeesRef.current = true;
    updateSettings.mutate(
      { params: { workflow_id: null }, data: pendingFeesToSettingsIn(pendingFees) },
      {
        onSuccess: () => {
          clearPendingFees();
          settingsQuery.refetch();
          invoicesQuery.refetch();
          toast.success("Applied the payment terms you set up earlier");
        },
        onError: () => {
          appliedPendingFeesRef.current = false;
        },
      },
    );
  }, [settingsLoading, settingsError, settingsData, pendingFees]);

  const eligibleCount = invoices.filter((i) => i.fee === "eligible").length;
  const appliedCount = invoices.filter((i) => i.fee === "applied").length;

  const money = (n: number, currency = orgCurrency) => formatMoney(n, currency, locale);

  const handleToggleEdit = () => {
    if (editing) {
      updateSettings.mutate(
        { params: { workflow_id: null }, data: policyDraftToSettingsIn(draft) },
        {
          onSuccess: () => {
            settingsQuery.refetch();
            toast.success("Policy updated");
          },
          onError: () => toast.error("Couldn't save the policy. Try again."),
        },
      );
    }
    setEditing((v) => !v);
  };

  return (
    <AppShell>
      {/* Header */}
      <div className="mb-6 flex flex-wrap items-center justify-between gap-3 md:mb-10">
        <h2 className="font-display text-2xl font-semibold tracking-tight text-foreground md:text-3xl">
          Invoices
        </h2>
        <Badge variant="success" size="pill" className="gap-1.5">
          <span className="relative flex size-1.5">
            <span className="absolute inline-flex size-full animate-ping rounded-full bg-success opacity-60" />
            <span className="relative inline-flex size-1.5 rounded-full bg-success" />
          </span>
          Connected to {account?.name ?? "Xero"}
        </Badge>
      </div>

      {/* Top row: policy + stats */}
      <div className="mb-6 grid gap-4 lg:grid-cols-[minmax(0,1.7fr)_minmax(0,1fr)]">
        <PolicyCard
          editing={editing}
          onToggle={handleToggleEdit}
          policy={editing ? draft : policy}
          setPolicy={setDraft}
          loading={settingsQuery.isLoading}
          saving={updateSettings.isPending}
          accounts={accounts}
          accountsLoading={accountsQuery.isLoading}
          currency={orgCurrency}
          locale={locale}
        />
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-1">
          <StatCard
            label="Eligible for a late fee"
            value={String(eligibleCount)}
            hint="Late fees applicable based on your policy"
            accent
          />
          <StatCard
            label="Late fees raised"
            value={String(appliedCount)}
            hint="Already applied in Xero"
          />
        </div>
      </div>

      {/* Table */}
      <Card>
        <div className="overflow-x-auto">
          <table className="w-full border-collapse text-sm">
            <thead>
              <tr className="border-b border-hairline text-left text-xs font-medium uppercase tracking-wide text-muted-foreground">
                <Th>Invoice no.</Th>
                <Th>Status</Th>
                <Th>Due date</Th>
                <Th className="text-right">Amount due</Th>
                <Th className="text-right">Days overdue</Th>
                <Th className="text-right">Eligible for late fee</Th>
              </tr>
            </thead>
            <tbody>
              {invoicesQuery.isLoading ? (
                <TableSkeletonRows />
              ) : invoicesQuery.isError ? (
                <tr>
                  <td colSpan={6} className="px-4 py-6 text-center text-sm text-muted-foreground">
                    Couldn't load invoices. Try refreshing.
                  </td>
                </tr>
              ) : (
                invoices.map((inv) => (
                  <tr
                    key={inv.id}
                    className="border-b border-hairline last:border-0 transition-colors hover:bg-secondary/40"
                  >
                    <Td>
                      <Link
                        to="/invoices/$id"
                        params={{ id: inv.id }}
                        className="font-medium text-primary hover:underline"
                      >
                        {inv.number}
                      </Link>
                      <div className="text-xs text-muted-foreground">
                        {inv.customer.name ?? "—"}
                      </div>
                    </Td>
                    <Td>
                      <Badge variant="warning" size="pill">
                        Overdue
                      </Badge>
                    </Td>
                    <Td className="text-foreground">{formatDate(inv.due)}</Td>
                    <Td className="text-right font-numeric">
                      {money(inv.amount_due, inv.currency)}
                    </Td>
                    <Td className="text-right font-numeric text-muted-foreground">
                      {inv.days_overdue ?? 0}
                    </Td>
                    <Td className="text-right">
                      <EligibilityPill state={inv.fee} onApply={() => setApplyInvoice(inv)} />
                    </Td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </Card>
      <ApplyLateFeeDialog
        invoice={applyInvoice}
        policy={policy}
        locale={locale}
        open={applyInvoice !== null}
        onOpenChange={(open) => !open && setApplyInvoice(null)}
        onApplied={() => invoicesQuery.refetch()}
      />
    </AppShell>
  );
}

function TableSkeletonRows() {
  return (
    <>
      {Array.from({ length: 4 }).map((_, i) => (
        <tr key={i} className="border-b border-hairline last:border-0">
          <Td>
            <Skeleton className="h-4 w-20" />
          </Td>
          <Td>
            <Skeleton className="h-5 w-16 rounded-full" />
          </Td>
          <Td>
            <Skeleton className="h-4 w-20" />
          </Td>
          <Td className="text-right">
            <Skeleton className="ml-auto h-4 w-16" />
          </Td>
          <Td className="text-right">
            <Skeleton className="ml-auto h-4 w-8" />
          </Td>
          <Td className="text-right">
            <Skeleton className="ml-auto h-6 w-24 rounded-full" />
          </Td>
        </tr>
      ))}
    </>
  );
}

function Th({ children, className }: { children: React.ReactNode; className?: string }) {
  return <th className={cn("px-4 py-3 font-medium", className)}>{children}</th>;
}
function Td({ children, className }: { children: React.ReactNode; className?: string }) {
  return <td className={cn("px-4 py-3 align-middle", className)}>{children}</td>;
}

function EligibilityPill({
  state,
  onApply,
}: {
  state: TreasureInvoiceOut["fee"];
  onApply: () => void;
}) {
  if (state === "eligible") {
    return (
      <button
        type="button"
        className="inline-flex items-center gap-1 rounded-full bg-primary-tint px-3 py-1 text-xs font-medium text-primary transition-colors hover:bg-primary hover:text-primary-foreground"
        onClick={onApply}
      >
        Apply late fee
      </button>
    );
  }
  if (state === "applied") {
    return (
      <span className="inline-flex items-center gap-1 rounded-full bg-success-tint px-3 py-1 text-xs font-medium text-success">
        <Check className="size-3" /> Late fee applied
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 rounded-full bg-hairline px-3 py-1 text-xs font-medium text-muted-foreground">
      Not eligible
    </span>
  );
}

function PolicyCard({
  editing,
  onToggle,
  policy,
  setPolicy,
  loading,
  saving,
  accounts,
  accountsLoading,
  currency,
  locale,
}: {
  editing: boolean;
  onToggle: () => void;
  policy: PolicyDraft;
  setPolicy: React.Dispatch<React.SetStateAction<PolicyDraft>>;
  loading?: boolean;
  saving?: boolean;
  accounts: Record<string, string>;
  accountsLoading?: boolean;
  currency: string;
  locale: string;
}) {
  const variableFeeLines = policy.fees.filter((f) =>
    VARIABLE_FEE_TYPES.includes(f.payment_fee_type),
  );
  const fixedFeeLines = policy.fees.filter((f) => FIXED_FEE_TYPES.includes(f.payment_fee_type));
  const feeMode: "fixed" | "tiered" = fixedFeeLines.length > 1 ? "tiered" : "fixed";

  const setVariableLines = (lines: FeeLineDraft[]) =>
    setPolicy((p) => ({
      ...p,
      fees: [
        ...p.fees.filter((f) => !VARIABLE_FEE_TYPES.includes(f.payment_fee_type)),
        ...lines.filter((f) => VARIABLE_FEE_TYPES.includes(f.payment_fee_type)),
      ],
    }));

  const setFixedLines = (lines: FeeLineDraft[]) =>
    setPolicy((p) => ({
      ...p,
      fees: [...p.fees.filter((f) => !FIXED_FEE_TYPES.includes(f.payment_fee_type)), ...lines],
    }));

  const setFeeMode = (mode: "fixed" | "tiered") => {
    const current = fixedFeeLines[0] ?? {
      payment_fee_type: PaymentFeeType.fixed_amount,
      amount: 0,
      days_after_due: 0,
      when_amount_above: null,
    };
    if (mode === "fixed") {
      setFixedLines([{ ...current, when_amount_above: null }]);
      return;
    }
    setFixedLines([
      { ...current, when_amount_above: null },
      { ...current, when_amount_above: 1000 },
    ]);
  };

  return (
    <Card className="p-5">
      <div className="mb-4 flex items-start justify-between gap-3">
        <div>
          <div className="font-display text-xl text-foreground">Your late fee policy</div>
        </div>
        <Button
          size="sm"
          variant={editing ? "default" : "secondary"}
          onClick={onToggle}
          disabled={saving}
        >
          {saving ? (
            "Saving…"
          ) : editing ? (
            "Done"
          ) : (
            <>
              <Pencil className="size-3.5" /> Edit
            </>
          )}
        </Button>
      </div>

      {loading ? (
        <div className="grid gap-4 sm:grid-cols-2">
          {Array.from({ length: 4 }).map((_, i) => (
            <Skeleton key={i} className="h-10 w-full" />
          ))}
        </div>
      ) : !editing ? (
        <div className="grid gap-4 sm:grid-cols-2">
          <ReadRow
            label="Interest"
            value={
              variableFeeLines.length > 0 ? (
                <FeeLinesReadOnly fees={variableFeeLines} currency={currency} locale={locale} />
              ) : (
                "—"
              )
            }
            className="sm:col-span-2"
          />
          <ReadRow
            label="One-off compensation"
            value={
              fixedFeeLines.length > 1 ? (
                <TieredFeeReadOnly tiers={fixedFeeLines} currency={currency} locale={locale} />
              ) : fixedFeeLines.length === 1 ? (
                <FeeLinesReadOnly fees={fixedFeeLines} currency={currency} locale={locale} />
              ) : (
                "—"
              )
            }
            className="sm:col-span-2"
          />
          <ReadRow
            label="Eligible after (days overdue)"
            value={String(policy.start_when_overdue_days)}
          />
          <ReadRow
            label="Account late fees to"
            value={accounts[policy.categorized_as] ?? policy.categorized_as}
          />
          <ReadRow label="Post to Xero as" value={policy.as_draft ? "Draft" : "Posted"} />
          <ReadRow
            label="Line description"
            value={
              <span className="font-numeric">
                {policy.description.split(/(\{\{number\}\})/).map((part, i) =>
                  part === "{{number}}" ? (
                    <span key={i} className="rounded bg-primary-tint px-1 py-0.5 text-primary">
                      {part}
                    </span>
                  ) : (
                    <React.Fragment key={i}>{part}</React.Fragment>
                  ),
                )}
              </span>
            }
            className="sm:col-span-2"
          />
        </div>
      ) : (
        <div className="grid gap-4">
          <div className="grid gap-2">
            <span className="text-xs font-medium text-muted-foreground">Interest</span>
            <FeeLinesEditor
              fees={variableFeeLines}
              onChange={setVariableLines}
              currency={currency}
              locale={locale}
              typeOptions={[PaymentFeeType.percent_pa, PaymentFeeType.bank_of_england_plus]}
            />
          </div>

          <div className="grid gap-2">
            <span className="text-xs font-medium text-muted-foreground">One-off compensation</span>
            <Segmented
              value={feeMode}
              onValueChange={setFeeMode}
              aria-label="One-off compensation type"
              options={[
                { value: "fixed", label: "Fixed" },
                { value: "tiered", label: "Tiered" },
              ]}
              className="w-[220px]"
            />
            {feeMode === "fixed" ? (
              <EditField label={`Amount (${currency})`} className="w-[220px]">
                <Input
                  type="number"
                  inputMode="decimal"
                  step="0.01"
                  value={fixedFeeLines[0]?.amount ?? 0}
                  onChange={(e) =>
                    setFixedLines([
                      {
                        payment_fee_type: PaymentFeeType.fixed_amount,
                        amount: Number(e.target.value) || 0,
                        days_after_due: fixedFeeLines[0]?.days_after_due ?? 0,
                        when_amount_above: null,
                      },
                    ])
                  }
                  className="font-numeric"
                />
              </EditField>
            ) : (
              <TieredFeeEditor
                tiers={fixedFeeLines}
                currency={currency}
                locale={locale}
                onChange={setFixedLines}
              />
            )}
          </div>

          <div className="grid gap-4 sm:grid-cols-2">
            <EditField label="Eligible after (days overdue)">
              <Input
                type="number"
                value={policy.start_when_overdue_days}
                onChange={(e) =>
                  setPolicy((p) => ({
                    ...p,
                    start_when_overdue_days: Math.max(0, Number(e.target.value) || 0),
                  }))
                }
                className="font-numeric"
              />
            </EditField>
            <EditField label="Account late fees to">
              <Select
                value={policy.categorized_as}
                onValueChange={(v) => setPolicy((p) => ({ ...p, categorized_as: v }))}
                disabled={accountsLoading}
              >
                <SelectTrigger>
                  <SelectValue
                    placeholder={accountsLoading ? "Loading accounts…" : "Select an account"}
                  />
                </SelectTrigger>
                <SelectContent>
                  {Object.entries(accounts).map(([id, name]) => (
                    <SelectItem key={id} value={id}>
                      {name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </EditField>
            <EditField label="Post to Xero as">
              <Segmented<"Draft" | "Posted">
                value={policy.as_draft ? "Draft" : "Posted"}
                onValueChange={(v) => setPolicy((p) => ({ ...p, as_draft: v === "Draft" }))}
                options={[
                  { value: "Draft", label: "Draft" },
                  { value: "Posted", label: "Posted" },
                ]}
              />
            </EditField>
            <EditField label="Line description" className="sm:col-span-2">
              <Input
                value={policy.description}
                onChange={(e) => setPolicy((p) => ({ ...p, description: e.target.value }))}
                className="font-numeric"
              />
              <span className="text-[11px] text-muted-foreground">
                Use{" "}
                <span className="rounded bg-primary-tint px-1 py-0.5 font-numeric text-primary">
                  {"{{number}}"}
                </span>{" "}
                — replaced with the invoice number at post time.
              </span>
            </EditField>
          </div>
        </div>
      )}
    </Card>
  );
}

function ReadRow({
  label,
  value,
  className,
}: {
  label: string;
  value: React.ReactNode;
  className?: string;
}) {
  return (
    <div className={cn("flex flex-col gap-1", className)}>
      <span className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
        {label}
      </span>
      <span className="text-sm text-foreground">{value}</span>
    </div>
  );
}

function EditField({
  label,
  children,
  className,
}: {
  label: string;
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <label className={cn("flex flex-col gap-1.5", className)}>
      <span className="text-xs font-medium text-muted-foreground">{label}</span>
      {children}
    </label>
  );
}

function formatDate(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
}

// Silence unused import warning when CardContent lands unused in some branches.
void CardContent;
